import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { adminGuideApi } from '../services/adminGuideApi';
import ModuleSidebar from './components/ModuleSidebar';
import FlowDiagram from './components/FlowDiagram';
import AlgorithmSteps from './components/AlgorithmSteps';
import EnvVarTable, { EndpointTable, RelatedFilesList } from './components/EnvVarTable';

function DocPanel({ doc }) {
  if (!doc) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        Select a module to view setup documentation.
      </Typography>
    );
  }

  const { content = {} } = doc;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        {doc.title}
      </Typography>
      {doc.summary && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {doc.summary}
        </Typography>
      )}
      <FlowDiagram chart={content.mermaid} title={doc.title} />
      <AlgorithmSteps steps={content.steps} />
      <EnvVarTable envVars={content.envVars} />
      <EndpointTable endpoints={content.endpoints} />
      <RelatedFilesList files={content.relatedFiles} />
    </Box>
  );
}

export default function AdminSetupGuidePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('overview');
  const [docTab, setDocTab] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    let mounted = true;

    adminGuideApi
      .listAll()
      .then((data) => {
        if (!mounted) return;
        const mods = data?.modules || [];
        setModules(mods);
        if (mods.length) {
          setSelectedModule((prev) =>
            mods.find((m) => m.module === prev) ? prev : mods[0].module
          );
        }
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'Failed to load admin guide');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const currentModule = useMemo(
    () => modules.find((m) => m.module === selectedModule),
    [modules, selectedModule]
  );

  const currentDoc = currentModule?.documents?.[docTab] || currentModule?.documents?.[0];

  useEffect(() => {
    setDocTab(0);
  }, [selectedModule]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 4 }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
          Admin Setup Guide
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Complete portal flow: auth, admin/user/robot setup, payment, storage, email, middleware, and subscription pipeline.
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 3 }, pt: 3 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}. Run npm run seed:admin-guide on the backend if the collection is empty.
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <ModuleSidebar
              modules={modules}
              selectedModule={selectedModule}
              onSelectModule={setSelectedModule}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              {currentModule && currentModule.documents?.length > 1 && (
                <>
                  <Tabs
                    value={docTab}
                    onChange={(_, v) => setDocTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2 }}
                  >
                    {currentModule.documents.map((doc, idx) => (
                      <Tab key={doc.slug} label={doc.title} value={idx} />
                    ))}
                  </Tabs>
                  <Divider sx={{ mb: 2 }} />
                </>
              )}
              <DocPanel doc={currentDoc} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
